/**
 * @fileoverview Report Notifications Store
 * @description Lightweight in-memory store for client-side report-ready
 *              notifications. Persists to sessionStorage so notifications
 *              survive soft navigation but are cleared when the tab closes.
 *
 *              Consumers subscribe via addEventListener / dispatchEvent on
 *              the window, which lets both the NotificationBell and the
 *              report page react without requiring a shared React context.
 */

const STORAGE_KEY = 'report_notifications';
export const REPORT_NOTIFY_EVENT = 'report-notification-change';

// In-memory fallback when sessionStorage is unavailable or full
let _memoryCache = null;

// ── Helpers ──────────────────────────────────────────────────

function load() {
  if (_memoryCache !== null) {
    try {
      // Try to sync back to sessionStorage on subsequent reads
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_memoryCache));
      const synced = _memoryCache;
      _memoryCache = null;
      return synced;
    } catch {
      return [..._memoryCache];
    }
  }
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function save(items) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    _memoryCache = null;
  } catch {
    // quota exceeded — fall back to in-memory cache
    _memoryCache = items;
  }
  window.dispatchEvent(new Event(REPORT_NOTIFY_EVENT));
}

// ── Public API ────────────────────────────────────────────────

export const reportNotificationStore = {
  /** Return all notifications (newest first) */
  getAll() {
    return load();
  },

  /** Return only unread */
  getUnread() {
    return load().filter((n) => !n.is_read);
  },

  /**
   * Push a new report-ready notification.
   * @param {{ label: string, csvRows: Array, runId?: number }} payload
   */
  add({ label, csvRows, runId }) {
    const items = load();
    const normalizedRunId = Number(runId || 0);
    if (normalizedRunId > 0 && items.some((n) => Number(n.runId || 0) === normalizedRunId)) {
      return;
    }

    items.unshift({
      id: `report_${Date.now()}`,
      label,
      csvRows,         // the full data needed to regenerate the CSV
      runId: normalizedRunId || null,
      is_read: false,
      created_at: new Date().toISOString(),
    });
    save(items);
  },

  /** Mark one as read */
  markRead(id) {
    const items = load().map((n) => (n.id === id ? { ...n, is_read: true } : n));
    save(items);
  },

  /** Mark all as read */
  markAllRead() {
    const items = load().map((n) => ({ ...n, is_read: true }));
    save(items);
  },

  /** Remove one */
  remove(id) {
    save(load().filter((n) => n.id !== id));
  },

  /** Remove all report notifications */
  clear() {
    save([]);
  },
};
